import type { EndPoint } from "../types/session.ts";
import { ModuleId } from "../types/session.ts";

export interface EndpointGroup {
  moduleId: number;
  moduleName: string;
  endpoints: EndPoint[];
}

const moduleNames: Record<number, string> = {
  [ModuleId.PX]: "PX",
  [ModuleId.Game]: "Game",
  [ModuleId.Rent]: "Rent",
  [ModuleId.Sale]: "Sale",
  [ModuleId.Unused]: "Unused",
  [ModuleId.GameRent]: "Game Rent",
  [ModuleId.Banner]: "Banner",
  [ModuleId.Statistics]: "Statistics",
  [ModuleId.Snus]: "Snus",
  [ModuleId.CompuShop]: "CompuShop",
  [ModuleId.Golf]: "Golf",
  [ModuleId.Access]: "Access",
  [ModuleId.AIM]: "AIM",
  [ModuleId.InstantInventory]: "Instant Inventory",
  [ModuleId.NeoCaddie]: "NeoCaddie",
  [ModuleId.PayaP]: "PayaP",
  [ModuleId.Robotics]: "Robotics",
};

export function getModuleName(moduleId: number): string {
  return moduleNames[moduleId] ?? `Module ${moduleId}`;
}

/** The single top "Global / Statistics" fleet endpoint (Instant Systems-wide view;
 *  currently id 26). Identified by the Statistics module — customer "*Global"
 *  endpoints (e.g. "Cantaloupe Global") do NOT carry it, so this cleanly excludes
 *  them. Prefer this over title matching ("global" substrings). */
export function isTopGlobalEndpoint(ep: { ModuleID?: number } | null | undefined): boolean {
  return ep?.ModuleID === ModuleId.Statistics;
}

/** A customer group's Global endpoint (Selecta Global, the three Cantaloupe Globals, …), which
 *  has no dataset of its own: the Analyst and agent prompts are kept off it. The server decides
 *  by the endpoint's database (`Access_%Global%`, the rule the SQL deploy marks a Global with,
 *  in IssaEndpointHasDataset) and holds every caller to it; the client has only the title, so it
 *  matches "Global" at the end, with or without a treaty letter (GlobalA, Global B). The top
 *  Statistics endpoint is not one (isTopGlobalEndpoint). */
export function isGroupEndpoint(ep: { Title?: string; ModuleID?: number } | null | undefined): boolean {
  return ep?.ModuleID === ModuleId.Access && /Global ?[A-Z]?$/i.test(ep.Title ?? "");
}

/** Whether an endpoint gets the Analyst (data) model in the Issa chat: Access-module
 *  endpoints only, not test copies (XY, Test). A group's Global only with its group
 *  dataset on (`groupDataset`: the IssaGroupDataset flag of the active endpoint's
 *  DbFeatureFlags, on unless false); the server then answers with the group model, and
 *  with the flag false with the Guide whatever is asked for. */
export function isAnalystEndpoint(
  ep: { Title: string; ModuleID: number } | null | undefined,
  groupDataset = false,
): boolean {
  if (!ep) return false;
  if (ep.ModuleID !== ModuleId.Access) return false;
  if (/(?:XY|Test)$/i.test(ep.Title)) return false;
  if (isGroupEndpoint(ep)) return groupDataset;
  return true;
}

/** How much of an endpoint's data the Issa chat reads: all of it (`full`), a group's combined
 *  dataset on its Global endpoint (`group`), or none, the Guide alone (`guide`). Follows
 *  isAnalystEndpoint, as the server does by the endpoint's database. */
export type IssaDataAccess = "full" | "group" | "guide";

export function issaDataAccess(
  ep: { Title: string; ModuleID: number } | null | undefined,
  groupDataset = false,
): IssaDataAccess {
  if (!isAnalystEndpoint(ep, groupDataset)) return "guide";
  return isGroupEndpoint(ep) ? "group" : "full";
}

/** AccessTest endpoint (id 11) — permitted to run the sales-wall overlay + rollup
 *  alongside the top global (26) for the TV-wall pilot. */
export const ACCESS_TEST_ENDPOINT_ID = 11;

/** IRS, our sister company in the US: its Admin Map shows every ship (WebGetVesselShipments). */
export const IRS_ENDPOINT_ID = 93;

/** ToolsXO portfolio endpoint whose map is backed by the combined Tools fleet. */
export const TOOLS_XO_ENDPOINT_ID = 125;

/** The Tools fleet by name. The scope itself is hardcoded server-side (AdminServerService
 *  polls these AIM databases for ToolsXO), and a ToolsXO operator does not necessarily
 *  carry the member endpoints in their own endpoint list — so the map needs its own names
 *  to label activity by customer rather than by a bare id. Kept beside the scope id so
 *  the two never drift apart. */
export const TOOLS_ENDPOINT_NAMES: Record<number, string> = {
  68: "ToolsSE",
  69: "ToolsFI",
  70: "ToolsNO",
  71: "ToolsSwedol",
  109: "ToolsXY",
  125: "ToolsXO",
};

/** A human label for an endpoint id. Prefers what the session actually knows, which is
 *  correct even when an endpoint is renamed; falls back to the Tools table, then to the
 *  id so a row is never blank. */
export function endpointLabel(
  endpointId: number,
  endpoints: readonly { ID: number; Title?: string; ShortName?: string }[] = [],
): string {
  const known = endpoints.find((endpoint) => endpoint.ID === endpointId);
  const fromSession = known?.ShortName?.trim() || known?.Title?.trim();
  if (fromSession) return fromSession;
  return TOOLS_ENDPOINT_NAMES[endpointId] ?? `ep ${endpointId}`;
}

/** The "IT-TV" wall account (user 4581) may see the sales wall without being a
 *  super admin. The server-side gate (SalesWallAccessPermission) is authoritative. */
export const SALES_WALL_USER_ID = 4581;

/** Endpoints allowed to show the sales wall: the top global (26) + AccessTest (11). */
export function isSalesWallEndpoint(
  ep: { ModuleID?: number; ID?: number } | null | undefined,
): boolean {
  return isTopGlobalEndpoint(ep) || ep?.ID === ACCESS_TEST_ENDPOINT_ID;
}

export function groupEndpoints(endpoints: EndPoint[], filter: string): EndpointGroup[] {
  const q = filter.toLowerCase();
  const filtered = q
    ? endpoints.filter(
        (ep) =>
          ep.Title?.toLowerCase().includes(q) ||
          ep.ShortName?.toLowerCase().includes(q)
      )
    : endpoints;

  const map = new Map<number, EndPoint[]>();
  for (const ep of filtered) {
    const list = map.get(ep.ModuleID);
    if (list) {
      list.push(ep);
    } else {
      map.set(ep.ModuleID, [ep]);
    }
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a - b)
    .map(([moduleId, eps]) => ({
      moduleId,
      moduleName: getModuleName(moduleId),
      endpoints: eps.slice().sort((a, b) => a.Title.localeCompare(b.Title)),
    }));
}
